/* ─────────────────────────────────────────────────────────────
   ModalParque.jsx — H1 (estructura) + H2 (validar en campo).
   Acciones H2.b/H3/H4/H5 llegan en iteraciones siguientes.
   ───────────────────────────────────────────────────────────── */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import PhotoPicker from "./PhotoPicker";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "info",        label: "Información",   testid: "tab-info",       icon: "fa-circle-info" },
  { id: "validar",     label: "Validar",       testid: "tab-validar",    icon: "fa-circle-check" },
  { id: "historial",   label: "Historial",     testid: "tab-historial",  icon: "fa-clock-rotate-left" },
  { id: "reportar",    label: "Reportar",      testid: "tab-reportar",   icon: "fa-triangle-exclamation" },
  { id: "tiporiego",   label: "Tipo de riego", testid: "tab-tiporiego",  icon: "fa-faucet-drip" },
];

const TIPOS_RIEGO = [
  { id: "RED_MORADA",       label: "Red morada",       descripcion: "Agua tratada por tubería municipal", icon: "fa-water",         color: "#7C3AED", bg: "#EDE9FE" },
  { id: "TINACO_CON_BOMBA", label: "Tinaco con bomba", descripcion: "Tinaco propio con bomba automática", icon: "fa-droplet",       color: "#0E7490", bg: "#CFFAFE" },
  { id: "REQUIERE_PIPA",    label: "Requiere pipa",    descripcion: "Necesita riego con pipa",            icon: "fa-truck-droplet", color: "#B45309", bg: "#FEF3C7" },
];

const CATEGORIAS_REP = [
  { id: "TUBERIA_ROTA", label: "Tubería rota",   icon: "fa-water" },
  { id: "SIN_PRESION",  label: "Sin presión",    icon: "fa-gauge-simple-low" },
  { id: "BOMBA_DANADA", label: "Bomba dañada",   icon: "fa-fan" },
  { id: "FUGA",         label: "Fuga",           icon: "fa-droplet" },
  { id: "VANDALISMO",   label: "Vandalismo",     icon: "fa-user-secret" },
  { id: "OTRO",         label: "Otro",           icon: "fa-circle-question" },
];

const URGENCIAS_META = {
  BAJA:  { color: "#15803D", bg: "#DCFCE7" },
  MEDIA: { color: "#B45309", bg: "#FEF3C7" },
  ALTA:  { color: "#B91C1C", bg: "#FEE2E2" },
};

const RESULTADOS_META = {
  FUNCIONA:    { label: "Funciona",     icon: "fa-circle-check",     color: "#15803D", bg: "#DCFCE7" },
  PARCIAL:     { label: "Parcial",      icon: "fa-circle-exclamation", color: "#B45309", bg: "#FEF3C7" },
  NO_FUNCIONA: { label: "No funciona",  icon: "fa-circle-xmark",     color: "#B91C1C", bg: "#FEE2E2" },
};

function formatFecha(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
}

function formatRelativo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)      return "hace unos segundos";
  if (diff < 3600)    return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400)   return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800)  return `hace ${Math.floor(diff / 86400)} días`;
  if (diff < 2592000) return `hace ${Math.floor(diff / 604800)} semanas`;
  return `hace ${Math.floor(diff / 2592000)} meses`;
}

/**
 * Muestra valor si existe; si null/undefined/"" devuelve dash.
 * No inventa valores.
 */
function val(v) {
  if (v === null || v === undefined || v === "") return "—";
  return v;
}

export default function ModalParque({ parque, onClose, onParqueActualizado }) {
  const [tab, setTab] = useState("info");
  // Estado local del parque para reflejar cambios in-modal sin esperar refresh global
  const [parqueLocal, setParqueLocal] = useState(parque);

  // Reset cuando cambia el parque (apertura nueva)
  useEffect(() => { setTab("info"); setParqueLocal(parque); }, [parque?.id]);  // eslint-disable-line

  // ESC + lock scroll
  useEffect(() => {
    if (!parque) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [parque, onClose]);

  if (!parque) return null;
  const parqueVisible = parqueLocal || parque;

  const gpsLink = (parqueVisible.gps_lat && parqueVisible.gps_lon)
    ? `https://www.google.com/maps?q=${parqueVisible.gps_lat},${parqueVisible.gps_lon}`
    : null;

  const activeTab = TABS.find(t => t.id === tab) || TABS[0];

  const handleParqueActualizado = (espacioActualizado) => {
    setParqueLocal(espacioActualizado);
    onParqueActualizado?.(espacioActualizado);
  };

  return (
    <div className="mp-bg" onClick={onClose} data-testid="modal-parque">
      <div className="mp-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="mp-title">
        {/* HEADER */}
        <header className="mp-head">
          <div className="mp-head-text">
            <h2 id="mp-title" className="mp-title">{parqueVisible.nombre || "(sin nombre)"}</h2>
            <div className="mp-id mono">{parqueVisible.id_oficial}</div>
            <div className="mp-badges">
              <span className="pill-tiny pill-zone">{parqueVisible.zona_id || "—"}</span>
              <span className="pill-tiny" style={{background:"#E0E7FF", color:"#3730A3"}}>
                <i className="fa-solid fa-location-dot mr-1"/>{val(parqueVisible.colonia)}
              </span>
              <span className="pill-tiny" style={{background:"#DCFCE7", color:"#166534"}} data-testid="badge-tipo-riego">
                <i className="fa-solid fa-faucet-drip mr-1"/>{val(parqueVisible.tipo_riego)}
              </span>
            </div>
            {gpsLink && (
              <a
                href={gpsLink}
                target="_blank"
                rel="noreferrer"
                className="mp-gps mono"
                data-testid="link-gps"
              >
                <i className="fa-solid fa-map-location-dot"/> Ver en mapa ({parqueVisible.gps_lat.toFixed(5)}, {parqueVisible.gps_lon.toFixed(5)})
              </a>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="mp-x"
            aria-label="Cerrar"
            data-testid="btn-x-modal"
          >
            <i className="fa-solid fa-xmark"/>
          </button>
        </header>

        {/* TABS */}
        <nav className="mp-tabs" role="tablist">
          {TABS.map(t => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              className={`mp-tab ${tab === t.id ? "mp-tab-active" : ""}`}
              onClick={() => setTab(t.id)}
              data-testid={t.testid}
            >
              <i className={`fa-solid ${t.icon} mr-1.5`}/>{t.label}
            </button>
          ))}
        </nav>

        {/* CUERPO */}
        <div className="mp-body" data-testid={`panel-${activeTab.id}`}>
          {tab === "info" ? (
            <TabInformacion parque={parqueVisible}/>
          ) : tab === "validar" ? (
            <TabValidar parque={parqueVisible} onGuardado={() => setTab("historial")}/>
          ) : tab === "historial" ? (
            <TabHistorial parque={parqueVisible}/>
          ) : tab === "reportar" ? (
            <TabReportar parque={parqueVisible} onCreado={onClose}/>
          ) : tab === "tiporiego" ? (
            <TabTipoRiego parque={parqueVisible} onActualizado={handleParqueActualizado}/>
          ) : (
            <Placeholder titulo={activeTab.label} mensaje={activeTab.placeholder}/>
          )}
        </div>

        {/* FOOTER */}
        <footer className="mp-foot">
          <SolicitarPipaBtn parque={parqueVisible}/>
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            style={{padding:"0.55rem 1rem"}}
            data-testid="btn-cerrar-modal"
          >
            Cerrar
          </button>
        </footer>
      </div>
    </div>
  );
}

/* ── Tab "Información" ───────────────────────────────────────── */
function TabInformacion({ parque }) {
  // Solo mostrar filas cuyo valor exista (no inventar)
  const filas = [
    ["ID oficial",          parque.id_oficial,            true],
    ["Nombre",              parque.nombre,                true],
    ["Zona",                parque.zona_id,               true],
    ["Colonia",             parque.colonia,               true],
    ["Tipo de espacio",     parque.tipo_espacio,          false],
    ["Categoría",           parque.categoria,             false],
    ["Sistema de riego",    parque.tipo_riego,            true],
    ["Requiere pipa",       parque.requiere_pipa,         false],
    ["Tipo de servicio",    parque.tipo_servicio_pipa,    false],
    ["Tipo de agua",        parque.tipo_agua_mayo26 || parque.tipo_agua, false],
    ["Prioridad",           parque.prioridad,             false],
    ["Frecuencia semanal",  parque.frecuencia_semanal,    false],
    ["Superficie (m²)",     parque.superficie_m2 ? Math.round(parque.superficie_m2).toLocaleString("es-MX") : null, false],
    ["GPS",                 (parque.gps_lat && parque.gps_lon) ? `${parque.gps_lat}, ${parque.gps_lon}` : null, false],
    ["Último servicio",     parque.fillout_ult_servicio,  false],
    ["Visitas registradas", parque.fillout_visitas,       false],
  ];

  return (
    <dl className="mp-info" data-testid="tab-info-content">
      {filas.map(([k, v, mostrarSiempre]) => {
        const ausente = v === null || v === undefined || v === "";
        if (ausente && !mostrarSiempre) return null;
        return (
          <div key={k} className="mp-info-row">
            <dt>{k}</dt>
            <dd className={ausente ? "mp-info-empty" : ""}>
              {ausente ? "—" : (typeof v === "boolean" ? (v ? "Sí" : "No") : String(v))}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/* ── Placeholder genérico para tabs futuros ─────────────────── */
function Placeholder({ titulo, mensaje }) {
  return (
    <div className="mp-placeholder">
      <i className="fa-solid fa-tools mp-placeholder-icon"/>
      <div className="mp-placeholder-title">{titulo}</div>
      <div className="mp-placeholder-msg">{mensaje}</div>
      <div className="mp-placeholder-tag">Próximamente</div>
    </div>
  );
}

/* ── Tab "Validar" (H2) ─────────────────────────────────────── */
const OPCIONES_RESULTADO = [
  { id: "FUNCIONA",     label: "Funciona",     descripcion: "Sistema completo y operando bien", icon: "fa-circle-check", color: "#15803D", bg: "#DCFCE7" },
  { id: "PARCIAL",      label: "Parcial",      descripcion: "Opera con fallas o cobertura incompleta", icon: "fa-circle-exclamation", color: "#B45309", bg: "#FEF3C7" },
  { id: "NO_FUNCIONA",  label: "No funciona",  descripcion: "Requiere atención · se sugerirá revisar", icon: "fa-circle-xmark", color: "#B91C1C", bg: "#FEE2E2" },
];

function TabValidar({ parque, onGuardado }) {
  const { user } = useAuth();
  const [resultado, setResultado] = useState(null);
  const [observaciones, setObservaciones] = useState("");
  const [fotos, setFotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const puedeValidar = user?.rol === "supervisor_areas" || user?.rol === "admin";

  const guardar = async () => {
    if (!resultado) { toast.error("Selecciona FUNCIONA, PARCIAL o NO FUNCIONA"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/validaciones-riego`, {
        espacio_id: parque.id,
        resultado,
        observaciones: observaciones.slice(0, 300),
        fotos: fotos.map(f => f.dataURL),
      });
      toast.success(`Validación guardada (${resultado}). El histórico se verá en la pestaña 'Historial' (próximamente).`);
      setResultado(null);
      setObservaciones("");
      setFotos([]);
      onGuardado?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar validación");
    } finally {
      setBusy(false);
    }
  };

  if (!puedeValidar) {
    return (
      <div className="mp-placeholder">
        <i className="fa-solid fa-lock mp-placeholder-icon"/>
        <div className="mp-placeholder-title">Sin permiso para validar</div>
        <div className="mp-placeholder-msg">Solo el supervisor de áreas contratadas (rol supervisor_areas) o el administrador pueden registrar validaciones.</div>
      </div>
    );
  }

  return (
    <div className="mp-validar" data-testid="tab-validar-content">
      <p className="mp-validar-q">¿El sistema de riego de este parque funciona?</p>

      <div className="mp-validar-opts">
        {OPCIONES_RESULTADO.map(o => (
          <button
            key={o.id}
            type="button"
            className="mp-opt"
            data-testid={`opt-${o.id}`}
            data-selected={resultado === o.id ? "true" : "false"}
            onClick={() => setResultado(o.id)}
            style={{
              borderColor: resultado === o.id ? o.color : "var(--border, #e2e8f0)",
              background: resultado === o.id ? o.bg : "transparent",
            }}
          >
            <i className={`fa-solid ${o.icon}`} style={{color: o.color, fontSize: "1.4rem"}}/>
            <div className="mp-opt-lbl" style={{color: resultado === o.id ? o.color : undefined}}>{o.label}</div>
            <div className="mp-opt-desc">{o.descripcion}</div>
          </button>
        ))}
      </div>

      <div className="mp-validar-obs">
        <label htmlFor="mp-obs" className="mp-validar-lbl">
          Observaciones <span className="mp-validar-opt">(opcional, máx 300)</span>
        </label>
        <textarea
          id="mp-obs"
          rows={3}
          maxLength={300}
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          placeholder="¿Qué encontraste en campo? Detalles que ayuden al supervisor general."
          data-testid="textarea-observaciones"
        />
        <div className="mp-validar-counter">{observaciones.length}/300</div>
      </div>

      <div className="mp-validar-fotos">
        <PhotoPicker fotos={fotos} setFotos={setFotos} max={3} label="Evidencia (opcional, máx 3)" testIdPrefix="foto-validar"/>
      </div>

      <button
        type="button"
        className="btn-primary mp-validar-submit"
        disabled={busy || !resultado}
        onClick={guardar}
        data-testid="btn-guardar-validacion"
      >
        {busy ? "Guardando…" : (
          <><i className="fa-solid fa-floppy-disk mr-1.5"/>Guardar validación</>
        )}
      </button>
    </div>
  );
}


/* ── Tab "Historial" (H2.b) ─────────────────────────────────── */
function TabHistorial({ parque }) {
  const [loading, setLoading] = useState(true);
  const [validaciones, setValidaciones] = useState([]);
  const [error, setError] = useState(null);
  const [fotoZoom, setFotoZoom] = useState(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/validaciones-riego`, { params: { espacio_id: parque.id } })
      .then(r => { if (!cancelado) setValidaciones(r.data.validaciones || []); })
      .catch(e => { if (!cancelado) setError(e.response?.data?.detail || "Error al cargar historial"); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [parque.id]);

  if (loading) {
    return (
      <div className="mp-placeholder" data-testid="historial-loading">
        <i className="fa-solid fa-spinner fa-spin mp-placeholder-icon"/>
        <div className="mp-placeholder-msg">Cargando histórico…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mp-placeholder" data-testid="historial-error">
        <i className="fa-solid fa-triangle-exclamation mp-placeholder-icon" style={{color: "#B91C1C"}}/>
        <div className="mp-placeholder-title">No se pudo cargar el histórico</div>
        <div className="mp-placeholder-msg">{error}</div>
      </div>
    );
  }

  if (validaciones.length === 0) {
    return (
      <div className="mp-placeholder" data-testid="historial-vacio">
        <i className="fa-solid fa-clock-rotate-left mp-placeholder-icon"/>
        <div className="mp-placeholder-title">Sin validaciones registradas</div>
        <div className="mp-placeholder-msg">Este parque aún no tiene validaciones registradas. Cuando un supervisor lo valide en campo, su registro aparecerá aquí.</div>
      </div>
    );
  }

  const ultima = validaciones[0]; // backend ya ordena fecha_validacion desc
  const metaUltima = RESULTADOS_META[ultima.resultado] || {};

  return (
    <div className="mp-historial" data-testid="tab-historial-content">
      <header className="mp-historial-head">
        <div>
          <div className="mp-historial-count" data-testid="historial-count">
            <strong>{validaciones.length}</strong> {validaciones.length === 1 ? "validación registrada" : "validaciones registradas"}
          </div>
          <div className="mp-historial-ultima">
            Última: <span className="pill-tiny" style={{background: metaUltima.bg, color: metaUltima.color}}>
              <i className={`fa-solid ${metaUltima.icon} mr-1`}/>{metaUltima.label}
            </span> · {formatFecha(ultima.fecha_validacion)}
          </div>
        </div>
      </header>

      <ol className="mp-timeline" data-testid="historial-timeline">
        {validaciones.map((v, i) => {
          const meta = RESULTADOS_META[v.resultado] || {};
          return (
            <li key={v.id} className="mp-tl-item" data-testid={`hist-item-${i}`}>
              <div className="mp-tl-dot" style={{background: meta.color}}>
                <i className={`fa-solid ${meta.icon}`}/>
              </div>
              <div className="mp-tl-card" style={{borderLeftColor: meta.color}}>
                <div className="mp-tl-card-head">
                  <span className="pill-tiny" style={{background: meta.bg, color: meta.color, fontWeight: 700}} data-testid={`hist-result-${i}`}>
                    <i className={`fa-solid ${meta.icon} mr-1`}/>{meta.label}
                  </span>
                  <span className="mp-tl-rel">{formatRelativo(v.fecha_validacion)}</span>
                </div>
                <div className="mp-tl-meta">
                  <i className="fa-solid fa-user mr-1"/>{v.validado_por_nombre || v.validado_por_usuario || "—"}
                  <span className="mp-tl-sep">·</span>
                  <i className="fa-regular fa-calendar mr-1"/>{formatFecha(v.fecha_validacion)}
                </div>
                {v.observaciones && (
                  <p className="mp-tl-obs" data-testid={`hist-obs-${i}`}>{v.observaciones}</p>
                )}
                {Array.isArray(v.fotos) && v.fotos.length > 0 && (
                  <div className="mp-tl-fotos" data-testid={`hist-fotos-${i}`}>
                    {v.fotos.map((src, j) => (
                      <button
                        key={j}
                        type="button"
                        className="mp-tl-foto-thumb"
                        onClick={() => setFotoZoom(src)}
                        aria-label={`Ampliar foto ${j + 1}`}
                        data-testid={`hist-foto-thumb-${i}-${j}`}
                      >
                        <img src={src} alt={`evidencia ${j + 1}`}/>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {fotoZoom && (
        <div className="mp-foto-zoom" onClick={() => setFotoZoom(null)} data-testid="foto-zoom">
          <img src={fotoZoom} alt="evidencia ampliada"/>
          <button type="button" className="mp-foto-zoom-x" aria-label="Cerrar">
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>
      )}
    </div>
  );
}


/* ── Tab "Reportar" (H3) ────────────────────────────────────── */
function TabReportar({ parque, onCreado }) {
  const { user } = useAuth();
  const [categoria, setCategoria] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [urgencia, setUrgencia] = useState("MEDIA");
  const [fotos, setFotos] = useState([]);
  const [requierePipa, setRequierePipa] = useState(false);
  const [busy, setBusy] = useState(false);

  const puedeReportar = user?.rol === "supervisor_areas" || user?.rol === "admin";
  const canSubmit = categoria && descripcion.trim().length > 0 && !busy;

  const enviar = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/tickets-areas`, {
        espacio_id: parque.id,
        categoria,
        descripcion: descripcion.slice(0, 500),
        urgencia,
        fotos: fotos.map(f => f.dataURL),
        requiere_pipa: requierePipa,
      });
      const folio = r.data.ticket.folio;
      const n = r.data.ticket.notificados?.length || 0;
      toast.success(`Reporte ${folio} creado. ${n} ${n === 1 ? "persona notificada" : "personas notificadas"}.`);
      setCategoria(""); setDescripcion(""); setUrgencia("MEDIA"); setFotos([]); setRequierePipa(false);
      onCreado?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear el reporte");
    } finally {
      setBusy(false);
    }
  };

  if (!puedeReportar) {
    return (
      <div className="mp-placeholder">
        <i className="fa-solid fa-lock mp-placeholder-icon"/>
        <div className="mp-placeholder-title">Sin permiso para reportar</div>
        <div className="mp-placeholder-msg">Solo el supervisor de áreas contratadas o el administrador pueden levantar reportes. Los reportes son visibles para Yanett (capturista) y los supervisores generales.</div>
      </div>
    );
  }

  return (
    <div className="mp-reportar" data-testid="tab-reportar-content">
      <p className="mp-validar-q">Levantar reporte de problema</p>
      <div className="mp-rep-hint">
        Se notificará a <strong>Yanett García</strong> (capturista) y a los <strong>supervisores generales</strong> (Saúl y Rogelio).
      </div>

      <div>
        <label htmlFor="mp-cat" className="mp-validar-lbl">Categoría del problema *</label>
        <select
          id="mp-cat"
          className="mp-select"
          value={categoria}
          onChange={(e) => setCategoria(e.target.value)}
          data-testid="select-categoria"
        >
          <option value="">— Selecciona categoría —</option>
          {CATEGORIAS_REP.map(c => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="mp-desc" className="mp-validar-lbl">Descripción *<span className="mp-validar-opt"> (máx 500)</span></label>
        <textarea
          id="mp-desc"
          rows={4}
          maxLength={500}
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder="Describe el problema: ubicación dentro del parque, gravedad, qué viste…"
          data-testid="textarea-descripcion"
        />
        <div className="mp-validar-counter">{descripcion.length}/500</div>
      </div>

      <div>
        <div className="mp-validar-lbl">Urgencia *</div>
        <div className="mp-rep-urg" role="radiogroup">
          {["BAJA","MEDIA","ALTA"].map(u => {
            const m = URGENCIAS_META[u];
            return (
              <button
                key={u}
                type="button"
                role="radio"
                aria-checked={urgencia === u}
                data-testid={`urg-${u}`}
                data-selected={urgencia === u ? "true" : "false"}
                onClick={() => setUrgencia(u)}
                className="mp-rep-urg-btn"
                style={{
                  borderColor: urgencia === u ? m.color : "var(--border, #e2e8f0)",
                  background:  urgencia === u ? m.bg    : "transparent",
                  color:       urgencia === u ? m.color : "var(--muted, #64748b)",
                }}
              >{u}</button>
            );
          })}
        </div>
      </div>

      <div>
        <PhotoPicker fotos={fotos} setFotos={setFotos} max={3} label="Evidencia (opcional, máx 3)" testIdPrefix="foto-reportar"/>
      </div>

      <label
        className="mp-rep-hint"
        style={{display:"flex", gap:"0.6rem", alignItems:"flex-start", cursor:"pointer", borderColor: requierePipa ? "#0284c7" : undefined, background: requierePipa ? "#E0F2FE" : undefined}}
        data-testid="label-requiere-pipa"
      >
        <input
          type="checkbox"
          checked={requierePipa}
          onChange={(e) => setRequierePipa(e.target.checked)}
          data-testid="checkbox-requiere-pipa"
          style={{marginTop: 3, transform: "scale(1.2)"}}
        />
        <span>
          <strong>Este problema requiere apoyo de pipa</strong> mientras se resuelve.
          <div className="text-xs text-slate-500 mt-0.5">Al marcarlo, el reporte aparecerá en la bandeja unificada del supervisor de pipas.</div>
        </span>
      </label>

      <button
        type="button"
        className="btn-primary mp-validar-submit"
        disabled={!canSubmit}
        onClick={enviar}
        data-testid="btn-levantar-reporte"
      >
        {busy ? "Enviando…" : (<><i className="fa-solid fa-paper-plane mr-1.5"/>Levantar reporte</>)}
      </button>
    </div>
  );
}

/* ── Tab "Tipo de riego" (H4) ───────────────────────────────── */
function TabTipoRiego({ parque, onActualizado }) {
  const { user } = useAuth();
  const [tipoNuevo, setTipoNuevo] = useState("");
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [loadingHist, setLoadingHist] = useState(true);

  const puedeCambiar = user?.rol === "supervisor_areas" || user?.rol === "admin";
  const tipoActual = parque.tipo_riego;
  const metaActual = TIPOS_RIEGO.find(t => t.id === tipoActual);
  const canSubmit = tipoNuevo && tipoNuevo !== tipoActual && motivo.trim().length > 0 && !busy;

  useEffect(() => {
    let cancelado = false;
    setLoadingHist(true);
    axios.get(`${API}/espacios/${parque.id}/historial-tipo-riego`)
      .then(r => { if (!cancelado) setHistorial(r.data.cambios || []); })
      .catch(() => { if (!cancelado) setHistorial([]); })
      .finally(() => { if (!cancelado) setLoadingHist(false); });
    return () => { cancelado = true; };
  }, [parque.id]);

  const guardar = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await axios.put(`${API}/espacios/${parque.id}/tipo-riego`, {
        tipo_nuevo: tipoNuevo,
        motivo: motivo.slice(0, 300),
      });
      const meta = TIPOS_RIEGO.find(t => t.id === r.data.tipo_nuevo);
      toast.success(
        `Tipo cambiado a "${meta?.label || r.data.tipo_nuevo}".` +
        (r.data.candidato_ruta_pipa ? " Marcado como candidato para ruta de pipas." : "") +
        (r.data.sugerencia_limpiada ? " Sugerencia de revisión limpiada." : "")
      );
      onActualizado?.(r.data.espacio);
      // recargar historial
      const h = await axios.get(`${API}/espacios/${parque.id}/historial-tipo-riego`);
      setHistorial(h.data.cambios || []);
      setTipoNuevo(""); setMotivo("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  if (!puedeCambiar) {
    return (
      <div className="mp-placeholder">
        <i className="fa-solid fa-lock mp-placeholder-icon"/>
        <div className="mp-placeholder-title">Sin permiso para cambiar tipo de riego</div>
        <div className="mp-placeholder-msg">Solo supervisor_areas o admin pueden modificar el tipo de riego de un parque.</div>
      </div>
    );
  }

  return (
    <div className="mp-tipo" data-testid="tab-tiporiego-content">
      <div>
        <div className="mp-validar-lbl">Tipo de riego actual</div>
        <div
          className="mp-tipo-actual"
          style={{background: metaActual?.bg || "#F1F5F9", color: metaActual?.color || "#475569"}}
          data-testid="tipo-riego-actual"
        >
          <i className={`fa-solid ${metaActual?.icon || "fa-circle-question"}`} style={{fontSize: "1.4rem"}}/>
          <div>
            <div className="mp-tipo-actual-lbl">{metaActual?.label || tipoActual || "Sin clasificar"}</div>
            {metaActual?.descripcion && <div className="mp-tipo-actual-desc">{metaActual.descripcion}</div>}
          </div>
        </div>
      </div>

      <div>
        <div className="mp-validar-lbl">Cambiar a</div>
        <div className="mp-tipo-opts">
          {TIPOS_RIEGO.filter(t => t.id !== tipoActual).map(t => (
            <button
              key={t.id}
              type="button"
              className="mp-opt"
              data-testid={`tipo-${t.id}`}
              data-selected={tipoNuevo === t.id ? "true" : "false"}
              onClick={() => setTipoNuevo(t.id)}
              style={{
                borderColor: tipoNuevo === t.id ? t.color : "var(--border, #e2e8f0)",
                background:  tipoNuevo === t.id ? t.bg : "transparent",
              }}
            >
              <i className={`fa-solid ${t.icon}`} style={{color: t.color, fontSize: "1.3rem"}}/>
              <div className="mp-opt-lbl" style={{color: tipoNuevo === t.id ? t.color : undefined}}>{t.label}</div>
              <div className="mp-opt-desc">{t.descripcion}</div>
            </button>
          ))}
        </div>
        {tipoNuevo === "REQUIERE_PIPA" && (
          <div className="mp-rep-hint" style={{marginTop: "0.6rem"}} data-testid="hint-requiere-pipa">
            <i className="fa-solid fa-info-circle mr-1"/>
            Al guardar, este parque se marcará como <strong>candidato para ruta de pipas</strong>
            para que el supervisor de pipas lo incluya en próximas rutas.
          </div>
        )}
      </div>

      <div>
        <label htmlFor="mp-tipo-mot" className="mp-validar-lbl">Motivo del cambio *<span className="mp-validar-opt"> (máx 300)</span></label>
        <textarea
          id="mp-tipo-mot"
          rows={3}
          maxLength={300}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="¿Por qué cambia el tipo de riego? Ej: bomba dañada irreparable, nueva instalación de red morada…"
          data-testid="textarea-motivo-tipo"
        />
        <div className="mp-validar-counter">{motivo.length}/300</div>
      </div>

      <button
        type="button"
        className="btn-primary mp-validar-submit"
        disabled={!canSubmit}
        onClick={guardar}
        data-testid="btn-guardar-tipo-riego"
      >
        {busy ? "Guardando…" : (<><i className="fa-solid fa-floppy-disk mr-1.5"/>Guardar cambio de tipo de riego</>)}
      </button>

      {/* Mini-historial */}
      {!loadingHist && historial.length > 0 && (
        <div className="mp-tipo-hist" data-testid="tipo-historial">
          <div className="mp-validar-lbl">Últimos cambios</div>
          <ol className="mp-tipo-hist-list">
            {historial.map((c, i) => {
              const mAnt = TIPOS_RIEGO.find(t => t.id === c.tipo_anterior);
              const mNue = TIPOS_RIEGO.find(t => t.id === c.tipo_nuevo);
              return (
                <li key={i} className="mp-tipo-hist-item" data-testid={`tipo-hist-${i}`}>
                  <div className="mp-tipo-hist-arrow">
                    <span className="pill-tiny" style={{background: mAnt?.bg || "#F1F5F9", color: mAnt?.color || "#475569"}}>{mAnt?.label || c.tipo_anterior || "—"}</span>
                    <i className="fa-solid fa-arrow-right mx-1.5 text-slate-400"/>
                    <span className="pill-tiny" style={{background: mNue?.bg, color: mNue?.color, fontWeight: 700}}>{mNue?.label || c.tipo_nuevo}</span>
                  </div>
                  <div className="mp-tipo-hist-meta">
                    {formatFecha(c.timestamp)} · <strong>{c.usuario_nombre || "—"}</strong>
                  </div>
                  {c.motivo && <p className="mp-tipo-hist-motivo">{c.motivo}</p>}
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}


/* ── Botón "Solicitar Pipa" del footer + sub-modal (H5) ─────── */
const MOTIVOS_PIPA = [
  { id: "SEQUIA",                label: "Falta de riego" },
  { id: "EVENTO_PROGRAMADO",     label: "Evento programado" },
  { id: "QUEJA_CIUDADANA",       label: "Queja ciudadana" },
  { id: "FUERA_DE_RUTA",         label: "Fuera de ruta habitual" },
  { id: "SISTEMA_PROPIO_FALLO",  label: "Sistema propio falló" },
  { id: "OTRO",                  label: "Otro" },
];

const TIPOS_AGUA_SOLICITUD = [
  { id: "TRATADA",          label: "Agua tratada (red morada)" },
  { id: "POTABLE",          label: "Agua potable" },
  { id: "TINACO_POTABLE",   label: "Tinaco potable" },
  { id: "TINACO_TRATADA",   label: "Tinaco tratada" },
];

/** Mapea el campo `tipo_agua` (+ `tipo_servicio_pipa`) del parque
 *  a uno de los 4 IDs del dropdown nuevo, o "" si no hay match seguro. */
function _inferirTipoAguaInicial(parque) {
  const tipoAgua = (parque.tipo_agua || "").toUpperCase().trim();
  const tipoServicio = (parque.tipo_servicio_pipa || "").toUpperCase().trim();
  const esLlenadoTinaco = tipoServicio.includes("TINACO");
  if (esLlenadoTinaco) {
    if (tipoAgua.includes("POTABLE")) return "TINACO_POTABLE";
    if (tipoAgua.includes("TRATADA")) return "TINACO_TRATADA";
  }
  if (tipoAgua === "TRATADA") return "TRATADA";
  if (tipoAgua === "POTABLE") return "POTABLE";
  return "";
}

/** Convierte el campo `requiere_pipa` del parque (string sucio) en boolean | null. */
function _inferirRequierePipaInicial(parque) {
  const raw = (parque.requiere_pipa || "").toString().toUpperCase().trim();
  if (raw.startsWith("NO")) return false;
  if (raw.startsWith("REQUIERE") || raw === "SI" || raw === "SÍ") return true;
  // Si tipo_riego del parque indica sistema propio, default NO.
  const t = (parque.tipo_riego || "").toUpperCase();
  if (t === "RED_MORADA" || t === "TINACO_CON_BOMBA" || t === "SISTEMA_RIEGO") return false;
  if (t === "PIPA" || t === "REQUIERE_PIPA") return true;
  return null;
}

function SolicitarPipaBtn({ parque }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const puede = user?.rol === "supervisor_areas" || user?.rol === "admin";

  if (!puede) {
    return (
      <button
        type="button"
        className="btn-primary"
        disabled
        title="Solo supervisor_areas o admin"
        data-testid="btn-solicitar-pipa"
      >
        <i className="fa-solid fa-truck-droplet mr-1.5"/>Solicitar Pipa
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        className="btn-primary"
        onClick={() => setOpen(true)}
        data-testid="btn-solicitar-pipa"
      >
        <i className="fa-solid fa-truck-droplet mr-1.5"/>Solicitar Pipa
      </button>
      {open && <SubModalSolicitarPipa parque={parque} onClose={() => setOpen(false)}/>}
    </>
  );
}

export function SubModalSolicitarPipa({ parque, onClose }) {
  // Detectar si el parque tiene riego propio funcional.
  // BD tiene 2 formatos para requiere_pipa: "NO_REQUIERE" (H4) y "NO REQUIERE PIPA" (legacy seed).
  const tipo = parque.tipo_riego;
  const reqNorm = (parque.requiere_pipa || "").toUpperCase().replace(/\s+/g, "_");
  const tieneRiegoPropio =
    tipo === "RED_MORADA" ||
    tipo === "TINACO_CON_BOMBA" ||
    reqNorm.startsWith("NO_REQUIERE");
  const tipoLabel =
    TIPOS_RIEGO.find(t => t.id === tipo)?.label ||
    (reqNorm.startsWith("NO_REQUIERE") ? "sistema propio" : (tipo || "sistema propio"));

  const [motivo, setMotivo] = useState("");
  // Pre-llenar urgencia con la prioridad del parque si es válida
  const inicialUrgencia = ["ALTA", "MEDIA", "BAJA"].includes((parque.prioridad || "").toUpperCase())
    ? parque.prioridad.toUpperCase()
    : "MEDIA";
  const [urgencia, setUrgencia] = useState(inicialUrgencia);
  // Campos nuevos (08-jun-2026): respuesta_requiere_pipa, tipo_agua_solicitada, observaciones
  const [requierePipa, setRequierePipa] = useState(() => _inferirRequierePipaInicial(parque));
  const [tipoAgua, setTipoAgua] = useState(() => _inferirTipoAguaInicial(parque));
  const [observaciones, setObservaciones] = useState("");
  const [comentario, setComentario] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const requierePipaListo = requierePipa === true || requierePipa === false;
  const tipoAguaListo = requierePipa === false ? true : !!tipoAgua;
  const canSubmit = motivo && requierePipaListo && tipoAguaListo && !busy;

  const enviar = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/solicitudes-pipa`, {
        espacio_id: parque.id,
        motivo, urgencia,
        comentario: comentario.slice(0, 200),
        respuesta_requiere_pipa: !!requierePipa,
        tipo_agua_solicitada: requierePipa ? (tipoAgua || null) : null,
        observaciones: (observaciones || "").slice(0, 500),
      });
      const s = r.data.solicitud;
      toast.success(
        `Solicitud ${s.folio} enviada. Score sugerido ${s.score_urgencia_sugerido}. ${s.notificados.length} supervisor(es) notificado(s).`
      );
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar solicitud");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mp-bg" onClick={onClose} data-testid="submodal-solicitar-pipa" style={{zIndex: 95}}>
      <div className="mp-card" onClick={(e) => e.stopPropagation()} style={{maxWidth: 480}}>
        <header className="mp-head">
          <div className="mp-head-text">
            <h2 className="mp-title"><i className="fa-solid fa-truck-droplet mr-2"/>Solicitar pipa</h2>
            <div className="mp-id">{parque.nombre} · <span className="mono">{parque.id_oficial}</span></div>
            <div className="mp-badges">
              <span className="pill-tiny pill-zone">{parque.zona_id || "—"}</span>
              {parque.prioridad && (
                <span className="pill-tiny" style={{background:"#FEF3C7", color:"#92400E", fontWeight: 700}} data-testid="pill-prioridad">
                  Prioridad {parque.prioridad}
                </span>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="mp-x" data-testid="btn-x-submodal"><i className="fa-solid fa-xmark"/></button>
        </header>
        <div className="mp-body" style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
          {tieneRiegoPropio && (
            <div className="mp-rep-hint" data-testid="aviso-riego-propio">
              <i className="fa-solid fa-info-circle mr-1"/>
              Este parque tiene riego propio (<strong>{tipoLabel}</strong>). Explica por qué necesita apoyo de pipa — el motivo es obligatorio.
            </div>
          )}
          <div>
            <label htmlFor="sm-mot" className="mp-validar-lbl">Motivo *</label>
            <select id="sm-mot" className="mp-select" value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="select-motivo-pipa">
              <option value="">— Selecciona motivo —</option>
              {MOTIVOS_PIPA.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <div className="mp-validar-lbl">Urgencia *<span className="mp-validar-opt"> (pre-llenada con prioridad del parque)</span></div>
            <div className="mp-rep-urg" role="radiogroup">
              {["BAJA","MEDIA","ALTA"].map(u => {
                const m = URGENCIAS_META[u];
                return (
                  <button
                    key={u}
                    type="button"
                    role="radio"
                    aria-checked={urgencia === u}
                    data-testid={`urg-pipa-${u}`}
                    onClick={() => setUrgencia(u)}
                    className="mp-rep-urg-btn"
                    style={{
                      borderColor: urgencia === u ? m.color : "var(--border, #e2e8f0)",
                      background:  urgencia === u ? m.bg    : "transparent",
                      color:       urgencia === u ? m.color : "var(--muted, #64748b)",
                    }}
                  >{u}</button>
                );
              })}
            </div>
          </div>

          {/* CAMPO A — ¿Requiere pipa? (toggle SÍ/NO) */}
          <div>
            <div className="mp-validar-lbl">
              ¿Requiere pipa? *
              <span className="mp-validar-opt"> (pre-llenado desde el parque)</span>
            </div>
            <div className="mp-rep-urg" role="radiogroup">
              {[
                { val: true,  lbl: "SÍ", color: "#15803D", bg: "#DCFCE7" },
                { val: false, lbl: "NO", color: "#1E40AF", bg: "#DBEAFE" },
              ].map(opt => (
                <button
                  key={String(opt.val)}
                  type="button"
                  role="radio"
                  aria-checked={requierePipa === opt.val}
                  data-testid={`req-pipa-${opt.lbl}`}
                  onClick={() => setRequierePipa(opt.val)}
                  className="mp-rep-urg-btn"
                  style={{
                    borderColor: requierePipa === opt.val ? opt.color : "var(--border, #e2e8f0)",
                    background:  requierePipa === opt.val ? opt.bg    : "transparent",
                    color:       requierePipa === opt.val ? opt.color : "var(--muted, #64748b)",
                  }}
                >{opt.lbl}</button>
              ))}
            </div>
          </div>

          {/* CAMPO B — Tipo de agua (solo si requierePipa === true) */}
          {requierePipa === true && (
            <div data-testid="campo-tipo-agua">
              <label htmlFor="sm-tipo-agua" className="mp-validar-lbl">
                Tipo de agua *
                <span className="mp-validar-opt"> (pre-llenado desde el parque)</span>
              </label>
              <select
                id="sm-tipo-agua"
                className="mp-select"
                value={tipoAgua}
                onChange={(e) => setTipoAgua(e.target.value)}
                data-testid="select-tipo-agua"
              >
                <option value="">— Selecciona tipo —</option>
                {TIPOS_AGUA_SOLICITUD.map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* CAMPO C — Observaciones adicionales (opcional, máx 500) */}
          <div>
            <label htmlFor="sm-obs" className="mp-validar-lbl">
              Observaciones adicionales
              <span className="mp-validar-opt"> (opcional, máx 500)</span>
            </label>
            <textarea
              id="sm-obs" rows={3} maxLength={500}
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Detalles que ayuden al supervisor de pipas a planear (acceso, horarios, contacto en sitio…)"
              data-testid="textarea-observaciones-pipa"
            />
            <div className="mp-validar-counter">{observaciones.length}/500</div>
          </div>

          <div>
            <label htmlFor="sm-com" className="mp-validar-lbl">Comentario <span className="mp-validar-opt">(opcional, máx 200)</span></label>
            <textarea
              id="sm-com" rows={3} maxLength={200}
              value={comentario} onChange={(e) => setComentario(e.target.value)}
              placeholder="¿Algo importante que considere el supervisor de pipas?"
              data-testid="textarea-comentario-pipa"
            />
            <div className="mp-validar-counter">{comentario.length}/200</div>
          </div>
        </div>
        <footer className="mp-foot">
          <button
            type="button"
            className="btn-primary"
            disabled={!canSubmit}
            onClick={enviar}
            data-testid="btn-enviar-solicitud-pipa"
          >
            {busy ? "Enviando…" : (<><i className="fa-solid fa-paper-plane mr-1.5"/>Enviar solicitud</>)}
          </button>
          <button type="button" className="btn-ghost" onClick={onClose} style={{padding:"0.55rem 1rem"}} data-testid="btn-cancelar-pipa">Cancelar</button>
        </footer>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────
   SubModalValidarParque v2 — Validación móvil-first (4 secciones).
   Independiente del flujo "Solicitar pipa".
   Endpoints: POST /api/espacios/{id}/validar
              POST /api/espacios/{id}/reportar-falla
   ───────────────────────────────────────────────────────────── */

/** Helpers de inferencia desde campos del parque ───────── */
function _inferirTipoAguaV2(parque) {
  const t = (parque.tipo_agua || "").toUpperCase();
  if (t === "POTABLE" || t === "TRATADA" || t === "AMBAS") return t;
  if (t.includes("POTABLE") && t.includes("TRATADA")) return "AMBAS";
  if (t.includes("POTABLE")) return "POTABLE";
  if (t.includes("TRATADA")) return "TRATADA";
  return "";
}
function _inferirAbastecimiento(parque) {
  const t = (parque.abastecimiento || parque.tipo_servicio_pipa || "").toUpperCase();
  if (t === "TINACO_ALIBE" || t === "MOTOBOMBA" || t === "AMBAS") return t;
  if (t.includes("AMBAS")) return "AMBAS";
  if (t.includes("TINACO") || t.includes("ALIBE") || t.includes("ALJIBE")) return "TINACO_ALIBE";
  if (t.includes("BOMBA") || t.includes("MOTO")) return "MOTOBOMBA";
  return "";
}

/** Botón grande tipo "tarjeta toggle" (mínimo 56 px alto). */
function PillBtn({ active, color, bg, onClick, children, disabled, testid }) {
  // Derivar color de sombra 3D: oscurecer ~20% del color de acento
  // Usamos inline style directo para conservar la paleta de color por-instancia (no hay clase fija).
  const shadowColor = color; // mismo tono — el `0 4px 0` ya lo hace más oscuro visualmente
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      disabled={disabled}
      onClick={onClick}
      data-testid={testid}
      style={{
        minHeight: 54,
        flex: 1,
        padding: "0.9rem 0.5rem",
        borderRadius: 13,
        border: `1.5px solid ${active ? color : "#CBD5E1"}`,
        background: active ? bg : "#FFFFFF",
        color: active ? color : "#475569",
        fontWeight: 700,
        fontSize: "0.9rem",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.15s ease",
        boxShadow: active
          ? `0 4px 0 ${shadowColor}, 0 6px 10px ${shadowColor}4D`  /* 4D = 30% opacity */
          : "0 3px 0 #CBD5E1, 0 4px 8px rgba(0,0,0,0.06)",
        opacity: disabled ? 0.5 : 1,
        userSelect: "none",
      }}
    >{children}</button>
  );
}

const SECCION_LBL_STYLE = {
  fontSize: "0.78rem",
  fontWeight: 800,
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: 6,
};

export function SubModalValidarParque({ parque, onClose, onValidado }) {
  // Sección 1: estado actual del riego
  const [estado, setEstado] = useState(
    ["BIEN", "MAL", "NO_FUNCIONA"].includes(parque.estado_riego) ? parque.estado_riego : ""
  );
  // Sección 2: tipo de agua
  const [tipoAgua, setTipoAgua] = useState(() => _inferirTipoAguaV2(parque));
  // Sección 3: abastecimiento
  const [abastecimiento, setAbastecimiento] = useState(() => _inferirAbastecimiento(parque));
  // Sección 4 (condicional)
  const [requiereAccion, setRequiereAccion] = useState("");
  const [fotoDataURL, setFotoDataURL] = useState(null);
  const [observaciones, setObservaciones] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyTipo, setBusyTipo] = useState(""); // "GUARDAR" | "REPORTAR"

  const fileRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  // Si vuelve a BIEN, limpiar sección 4
  useEffect(() => {
    if (estado === "BIEN") {
      setRequiereAccion("");
    }
  }, [estado]);

  const mostrarSec4 = estado === "MAL" || estado === "NO_FUNCIONA";
  const fotoObligatoria = estado === "NO_FUNCIONA";
  const camposCompletos =
    !!estado && !!tipoAgua && !!abastecimiento &&
    (!mostrarSec4 || !!requiereAccion) &&
    (!fotoObligatoria || !!fotoDataURL);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Selecciona una imagen");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("La foto es muy grande (máx 8 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFotoDataURL(reader.result);
    reader.onerror = () => toast.error("Error al leer la imagen");
    reader.readAsDataURL(f);
  };

  const payload = () => ({
    estado_riego: estado,
    tipo_agua: tipoAgua,
    abastecimiento,
    requiere_accion: mostrarSec4 ? (requiereAccion || null) : null,
    foto_data_url: fotoDataURL,
    observaciones: (observaciones || "").slice(0, 500),
  });

  const guardar = async () => {
    if (!camposCompletos) return;
    setBusy(true); setBusyTipo("GUARDAR");
    try {
      const r = await axios.post(`${API}/espacios/${parque.id}/validar`, payload());
      toast.success("Parque actualizado correctamente");
      if (onValidado) onValidado(r.data.espacio);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar la validación");
    } finally {
      setBusy(false); setBusyTipo("");
    }
  };

  const reportarFalla = async () => {
    if (!camposCompletos) return;
    setBusy(true); setBusyTipo("REPORTAR");
    try {
      const r = await axios.post(`${API}/espacios/${parque.id}/reportar-falla`, payload());
      toast.success("Falla reportada correctamente");
      if (onValidado) onValidado(r.data.espacio);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al reportar la falla");
    } finally {
      setBusy(false); setBusyTipo("");
    }
  };

  return (
    <div
      className="mp-bg"
      data-testid="modal-validar-overlay"
      onClick={(e) => e.target.classList.contains("mp-bg") && !busy && onClose()}
    >
      <div
        className="mp-card"
        role="dialog"
        aria-modal="true"
        data-testid="modal-validar"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 540, width: "100%" }}
      >
        <header className="mp-head" style={{ padding: "1rem 1.1rem", borderBottom: "1px solid #E2E8F0" }}>
          <div className="mp-head-text">
            <h3 className="text-lg font-bold m-0" style={{ color: "#0F172A" }}>
              <i className="fa-solid fa-circle-check mr-2 text-emerald-600"/>
              Validar parque — {parque.nombre}
            </h3>
            <div className="mp-id mono" style={{ color: "#64748B", fontSize: 12, marginTop: 2 }}>
              {parque.id_oficial || "—"} · {parque.zona_id || "—"}
            </div>
          </div>
          <button type="button" className="mp-close" onClick={onClose} aria-label="Cerrar" disabled={busy} data-testid="btn-cerrar-validar">
            <i className="fa-solid fa-xmark"/>
          </button>
        </header>

        <div style={{ display: "grid", gap: "1.1rem", padding: "1rem 1.1rem", maxHeight: "70vh", overflowY: "auto" }}>
          {/* SECCIÓN 1 — ¿Cómo está ahora? */}
          <section data-testid="val-seccion-1">
            <div style={SECCION_LBL_STYLE}>1️⃣ ¿Cómo está ahora?</div>
            <div style={{ display: "flex", gap: 8 }}>
              <PillBtn active={estado === "BIEN"}        color="#15803D" bg="#DCFCE7" onClick={() => setEstado("BIEN")}        testid="val-estado-BIEN">✅ Bien</PillBtn>
              <PillBtn active={estado === "MAL"}         color="#B45309" bg="#FEF3C7" onClick={() => setEstado("MAL")}         testid="val-estado-MAL">⚠️ Mal</PillBtn>
              <PillBtn active={estado === "NO_FUNCIONA"} color="#B91C1C" bg="#FEE2E2" onClick={() => setEstado("NO_FUNCIONA")} testid="val-estado-NO_FUNCIONA">❌ No funciona</PillBtn>
            </div>
          </section>

          {/* SECCIÓN 2 — ¿Qué agua tiene? */}
          <section data-testid="val-seccion-2">
            <div style={SECCION_LBL_STYLE}>2️⃣ ¿Qué agua tiene?</div>
            <div style={{ display: "flex", gap: 8 }}>
              <PillBtn active={tipoAgua === "POTABLE"} color="#1E40AF" bg="#DBEAFE" onClick={() => setTipoAgua("POTABLE")} testid="val-agua-POTABLE">💧 Potable</PillBtn>
              <PillBtn active={tipoAgua === "TRATADA"} color="#7C3AED" bg="#EDE9FE" onClick={() => setTipoAgua("TRATADA")} testid="val-agua-TRATADA">🌊 Tratada</PillBtn>
              <PillBtn active={tipoAgua === "AMBAS"}   color="#0F766E" bg="#CCFBF1" onClick={() => setTipoAgua("AMBAS")}   testid="val-agua-AMBAS">💧🌊 Ambas</PillBtn>
            </div>
          </section>

          {/* SECCIÓN 3 — ¿Cómo se abastece? */}
          <section data-testid="val-seccion-3">
            <div style={SECCION_LBL_STYLE}>3️⃣ ¿Cómo se abastece?</div>
            <div style={{ display: "flex", gap: 8 }}>
              <PillBtn active={abastecimiento === "TINACO_ALIBE"} color="#0369A1" bg="#E0F2FE" onClick={() => setAbastecimiento("TINACO_ALIBE")} testid="val-ab-TINACO_ALIBE">💧 Tinaco/Alibe</PillBtn>
              <PillBtn active={abastecimiento === "MOTOBOMBA"}    color="#9333EA" bg="#F3E8FF" onClick={() => setAbastecimiento("MOTOBOMBA")}    testid="val-ab-MOTOBOMBA">⚙️ Motobomba</PillBtn>
              <PillBtn active={abastecimiento === "AMBAS"}        color="#0F766E" bg="#CCFBF1" onClick={() => setAbastecimiento("AMBAS")}        testid="val-ab-AMBAS">💧⚙️ Ambas</PillBtn>
            </div>
          </section>

          {/* SECCIÓN 4 — ¿Requiere acción? (condicional) */}
          {mostrarSec4 && (
            <section data-testid="val-seccion-4" style={{
              background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 12, padding: "0.85rem",
            }}>
              <div style={SECCION_LBL_STYLE}>4️⃣ ¿Requiere acción?</div>

              <label htmlFor="val-req" style={{ display: "block", marginBottom: 12 }}>
                <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#475569" }}>¿Qué requiere?</span>
                <select
                  id="val-req"
                  className="mp-select"
                  value={requiereAccion}
                  onChange={(e) => setRequiereAccion(e.target.value)}
                  data-testid="val-requiere-accion"
                  style={{ width: "100%", marginTop: 4, padding: "0.65rem", fontSize: "0.95rem" }}
                >
                  <option value="">— Selecciona —</option>
                  <option value="PIPA">PIPA (llenar tinaco/alibe)</option>
                  <option value="REPARAR_MOTOBOMBA">REPARAR MOTOBOMBA</option>
                  <option value="AMBAS">AMBAS</option>
                  <option value="OTRO">OTRO</option>
                </select>
              </label>

              {/* Foto */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#475569", marginBottom: 6 }}>
                  Foto {fotoObligatoria ? <span style={{ color: "#B91C1C" }}>* (obligatoria)</span> : <span style={{ color: "#64748B" }}>(opcional)</span>}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={onFileChange}
                  style={{ display: "none" }}
                  data-testid="val-foto-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="val-foto-btn"
                  style={{
                    width: "100%", minHeight: 56, padding: "0.85rem",
                    background: fotoDataURL ? "#DCFCE7" : "#FFFFFF",
                    border: `2px dashed ${fotoDataURL ? "#15803D" : "#94A3B8"}`,
                    color: fotoDataURL ? "#15803D" : "#475569",
                    borderRadius: 12, fontWeight: 700, fontSize: "0.95rem", cursor: "pointer",
                  }}
                >
                  <i className={`fa-solid ${fotoDataURL ? "fa-camera-retro" : "fa-camera"} mr-2`}/>
                  {fotoDataURL ? "Cambiar foto" : "📷 Tomar foto"}
                </button>
                {fotoDataURL && (
                  <div style={{ position: "relative", marginTop: 8 }} data-testid="val-foto-preview">
                    <img
                      src={fotoDataURL}
                      alt="Vista previa"
                      style={{ width: "100%", borderRadius: 10, maxHeight: 220, objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      onClick={() => setFotoDataURL(null)}
                      data-testid="val-foto-eliminar"
                      style={{
                        position: "absolute", top: 6, right: 6,
                        background: "rgba(0,0,0,0.6)", color: "white", border: 0,
                        width: 32, height: 32, borderRadius: 99, cursor: "pointer",
                      }}
                    >
                      <i className="fa-solid fa-trash"/>
                    </button>
                  </div>
                )}
              </div>

              {/* Notas / observaciones */}
              <div>
                <label htmlFor="val-obs" style={{ display: "block" }}>
                  <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#475569" }}>Notas / observaciones</span>
                  <textarea
                    id="val-obs"
                    rows={3}
                    maxLength={500}
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    placeholder="Observaciones del estado del parque..."
                    data-testid="textarea-observaciones-val"
                    style={{ width: "100%", marginTop: 4, padding: "0.6rem", borderRadius: 8, border: "1px solid #CBD5E1", fontSize: "0.9rem", fontFamily: "inherit" }}
                  />
                </label>
                <div className="mp-validar-counter" style={{ textAlign: "right" }}>{observaciones.length}/500</div>
              </div>
            </section>
          )}

          {/* Si BIEN, mostrar campo de observaciones opcional fuera de la sección 4 */}
          {estado === "BIEN" && (
            <section>
              <div style={SECCION_LBL_STYLE}>Observaciones (opcional)</div>
              <textarea
                rows={3}
                maxLength={500}
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Observaciones del estado del parque..."
                data-testid="textarea-observaciones-val-bien"
                style={{ width: "100%", padding: "0.6rem", borderRadius: 8, border: "1px solid #CBD5E1", fontSize: "0.9rem", fontFamily: "inherit" }}
              />
              <div className="mp-validar-counter" style={{ textAlign: "right" }}>{observaciones.length}/500</div>
            </section>
          )}
        </div>

        <footer style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0.9rem 1.1rem 1.1rem", borderTop: "1px solid #E2E8F0", background: "#F8FAFC" }}>
          <button
            type="button"
            onClick={guardar}
            disabled={!camposCompletos || busy}
            data-testid="btn-guardar-val"
            style={{
              width: "100%", minHeight: 56,
              background: camposCompletos ? "#16A34A" : "#94A3B8",
              color: "white", border: 0, padding: "0.85rem", borderRadius: 12,
              fontWeight: 800, fontSize: "1rem", cursor: camposCompletos ? "pointer" : "not-allowed",
              boxShadow: camposCompletos ? "0 8px 22px -10px rgba(22,163,74,0.6)" : "none",
            }}
          >
            {busyTipo === "GUARDAR" ? "Guardando…" : <><i className="fa-solid fa-floppy-disk mr-2"/>GUARDAR</>}
          </button>
          {mostrarSec4 && (
            <button
              type="button"
              onClick={reportarFalla}
              disabled={!camposCompletos || busy}
              data-testid="btn-reportar-falla"
              style={{
                width: "100%", minHeight: 56,
                background: camposCompletos ? "#DC2626" : "#94A3B8",
                color: "white", border: 0, padding: "0.85rem", borderRadius: 12,
                fontWeight: 800, fontSize: "1rem", cursor: camposCompletos ? "pointer" : "not-allowed",
              }}
            >
              {busyTipo === "REPORTAR" ? "Enviando…" : <><i className="fa-solid fa-triangle-exclamation mr-2"/>REPORTAR FALLA</>}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="btn-cancelar-val"
            style={{
              width: "100%", minHeight: 44,
              background: "transparent", color: "#64748B",
              border: "1px solid #CBD5E1", borderRadius: 12, fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
            }}
          >Cancelar</button>
        </footer>
      </div>
    </div>
  );
}


